import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Minus, Plus, X, MapPin, Tag } from "lucide-react";
import api, { inr, formatApiError } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { Reveal } from "../components/Reveal";

const FREE_SHIP = 1999;
const MAX_QTY = 10;
const inputCls = "w-full bg-transparent border border-vv-black/20 focus:border-vv-black outline-none px-4 py-3 text-sm placeholder:text-vv-stone transition-colors";

function Steps({ step }) {
  const items = [["verify", "Mobile Verification"], ["address", "Delivery Address"], ["payment", "Payment"]];
  const idx = items.findIndex(([k]) => k === step);
  return (
    <div data-testid="checkout-steps" className="flex items-center mb-10 md:mb-14">
      {items.map(([key, label], i) => (
        <div key={key} className="flex items-center">
          <div className={`flex items-center gap-2.5 ${i <= idx ? "text-vv-black" : "text-vv-stone"}`}>
            <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-[10px] shrink-0 ${
              i < idx ? "bg-vv-forest border-vv-forest text-vv-ivory" : i === idx ? "border-vv-black" : "border-vv-stone/50"}`}>
              {i < idx ? "✓" : i + 1}
            </span>
            <span className={`text-[9px] sm:text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.2em] whitespace-nowrap ${i === idx ? "" : "hidden sm:inline"}`}>{label}</span>
          </div>
          {i < items.length - 1 && <span className={`w-4 mx-2 sm:w-14 sm:mx-3 h-px ${i < idx ? "bg-vv-forest" : "bg-vv-stone/40"}`} />}
        </div>
      ))}
    </div>
  );
}

function VerifyStep({ onVerified }) {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("phone");
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentOk, setSentOk] = useState(false);
  const reqId = useRef(null);
  const [widgetReady, setWidgetReady] = useState(false);
  const WIDGET_ID = "366b62703156323534333239";
  const WIDGET_TOKEN = process.env.REACT_APP_MSG91_WIDGET_TOKEN;
  const smsDown = !WIDGET_TOKEN;

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setInterval(() => setSeconds((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [seconds]);

  useEffect(() => {
    if (!WIDGET_TOKEN) return;
    if (!document.getElementById("msg91-otp-script")) {
      const script = document.createElement("script");
      script.id = "msg91-otp-script";
      script.src = "https://verify.msg91.com/otp-provider.js";
      script.async = true;
      script.onload = () => {
        try {
          window.initSendOTP({
            widgetId: WIDGET_ID,
            tokenAuth: WIDGET_TOKEN,
            exposeMethods: true,
            isPlainCaptcha: false,
            success: () => {},
            failure: () => { setError("OTP service could not start. Please refresh and try again."); },
          });
        } catch { setError("OTP service failed to start. Please refresh and try again."); }
      };
      script.onerror = () => setError("Unable to load the OTP service. Check your connection and retry.");
      document.head.appendChild(script);
    }
    let tries = 0;
    const poll = setInterval(() => {
      tries += 1;
      if (typeof window.sendOtp === "function" && typeof window.verifyOtp === "function") {
        setWidgetReady(true);
        clearInterval(poll);
      } else if (tries > 40) {
        clearInterval(poll);
      }
    }, 500);
    return () => clearInterval(poll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setSentOk(false);
    if (!widgetReady) {
      setBusy(false);
      setError("OTP service is still loading — please try again in a moment.");
      return;
    }
    window.sendOtp(`91${phone}`,
      (data) => {
        reqId.current = data?.message || null;
        setStage("otp");
        setSeconds(30);
        setSentOk(true);
        setBusy(false);
      },
      (err) => {
        setBusy(false);
        setError(err?.message || "OTP could not be sent. Check the number and try again.");
      });
  };

  const verify = (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    window.verifyOtp(otp, async (data) => {
      const accessToken = data?.access_token || data?.accessToken || data?.message;
      if (!accessToken) { setBusy(false); setError("Verification failed. Please try again."); return; }
      try {
        await api.post("/auth/otp/widget-verify", { access_token: accessToken, phone });
        const { data: me } = await api.get("/auth/me");
        toast.success("Mobile number verified");
        onVerified(me);
      } catch (err) {
        setError(formatApiError(err));
      } finally { setBusy(false); }
    }, () => {
      setBusy(false);
      setError("Incorrect or expired OTP. Please try again.");
    }, reqId.current);
  };

  const resend = () => {
    setBusy(true); setError("");
    const done = () => { setSeconds(30); setBusy(false); toast.success("OTP resent"); };
    if (typeof window.retryOtp === "function") {
      window.retryOtp("text", done, () => { setBusy(false); setError("Could not resend OTP. Please try again."); });
    } else {
      window.sendOtp(`91${phone}`, done, () => { setBusy(false); setError("Could not resend OTP. Please try again."); });
    }
  };

  return (
    <div className="max-w-md">
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-3">VERIFY YOUR MOBILE</h1>
      <p className="text-sm text-vv-muted mb-10">We will send a one-time password by SMS to keep your order and account secure.</p>
      {stage === "phone" ? (
        <form onSubmit={send} className="space-y-6">
          <div className="flex">
            <span className="border border-r-0 border-vv-black/20 px-4 py-3 text-sm text-vv-muted bg-vv-elevated/50">+91</span>
            <input data-testid="otp-phone-input" required inputMode="numeric" maxLength={10}
              value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              placeholder="10-digit mobile number" className={`${inputCls} flex-1 min-w-0`} />
          </div>
          {error && <p data-testid="otp-error" className="text-xs text-vv-burgundy">{error}</p>}
          <button data-testid="otp-send-button" type="submit" disabled={busy || phone.length !== 10 || smsDown} className="vv-btn-dark w-full disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Send OTP
          </button>
          {smsDown && (
            <p data-testid="otp-config-error" className="text-[11px] text-vv-muted">
              SMS verification is not configured yet. Meanwhile you can <Link data-testid="otp-email-fallback" to="/auth" className="vv-link-underline text-vv-black">sign in with email</Link> and continue checkout.
            </p>
          )}
          <p className="text-[11px] text-vv-stone">By continuing you agree to our terms and privacy policy.</p>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-6">
          <p className="text-sm text-vv-muted">OTP sent to <span className="text-vv-black font-medium">+91 {phone}</span>. Valid for a few minutes.</p>
          {sentOk && <p data-testid="otp-sent-success" className="text-xs text-vv-forest">SMS sent — check your messages.</p>}
          <input data-testid="otp-code-input" required inputMode="numeric" maxLength={6} autoFocus autoComplete="one-time-code"
            value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="6-digit OTP" className={`${inputCls} tracking-[0.5em] text-center text-lg font-mono`} />
          {error && <p data-testid="otp-error" className="text-xs text-vv-burgundy">{error}</p>}
          <button data-testid="otp-verify-button" type="submit" disabled={busy || otp.length !== 6} className="vv-btn-dark w-full disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Verify & Continue
          </button>
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em]">
            <button type="button" data-testid="otp-change-number" onClick={() => { setStage("phone"); setOtp(""); setError(""); setSentOk(false); }} className="text-vv-muted hover:text-vv-black">
              Change Number
            </button>
            <button type="button" data-testid="otp-resend-button" onClick={resend} disabled={busy || seconds > 0}
              className="text-vv-muted hover:text-vv-black disabled:opacity-40">
              {seconds > 0 ? `Resend in ${seconds}s` : "Resend OTP"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export function AddressFields({ address, setAddress, lockPhone = true }) {
  const [pinState, setPinState] = useState({ loading: false, error: "", offices: [] });
  const set = (k) => (e) => setAddress({ ...address, [k]: e.target.value });

  useEffect(() => {
    const pin = address.pin;
    if (!/^[1-9]\d{5}$/.test(pin)) {
      if (pinState.error || pinState.offices.length) setPinState({ loading: false, error: pin.length === 6 ? "Enter a valid Indian PIN code." : "", offices: [] });
      if (address.city || address.state) setAddress((a) => ({ ...a, city: "", state: "", locality: "" }));
      return;
    }
    let cancelled = false;
    setPinState({ loading: true, error: "", offices: [] });
    api.get(`/pincode/${pin}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (!data.valid) {
          setPinState({ loading: false, error: data.message, offices: [] });
          setAddress((a) => ({ ...a, city: "", state: "", locality: "" }));
        } else {
          setPinState({ loading: false, error: "", offices: data.offices || [], serviceable: data.serviceable });
          setAddress((a) => ({ ...a, city: data.district || "", state: data.state || "", locality: data.offices?.[0]?.name || "" }));
        }
      })
      .catch(() => { if (!cancelled) setPinState({ loading: false, error: "PIN lookup unavailable. Try again.", offices: [] }); });
    return () => { cancelled = true; };
  }, [address.pin]);

  const canProceed = address.name && address.phone?.length === 10 && /^[1-9]\d{5}$/.test(address.pin)
    && address.city && address.state && address.house && address.street && !pinState.loading && !pinState.error;

  const label = address.label || "Home";
  const preset = ["Home", "Office"].includes(label) ? label : label ? "Other" : "Home";

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-vv-muted mb-2.5">Save this address as</p>
        <div className="flex flex-wrap gap-2 items-center">
          {["Home", "Office", "Other"].map((l) => (
            <button key={l} type="button" data-testid={`addr-label-${l.toLowerCase()}`}
              onClick={() => setAddress({ ...address, label: l === "Other" ? "" : l })}
              className={`text-[10px] uppercase tracking-[0.2em] border px-3.5 py-2 transition-colors ${preset === l ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20 hover:border-vv-black"}`}>
              {l}
            </button>
          ))}
          {preset === "Other" && (
            <input data-testid="addr-label-custom" value={label} maxLength={20}
              onChange={(e) => setAddress({ ...address, label: e.target.value })}
              placeholder="e.g. Mom's Place" className="bg-transparent border-b border-vv-black/25 focus:border-vv-black outline-none px-1 py-1.5 text-sm w-40 placeholder:text-vv-stone" />
          )}
        </div>
      </div>
      <input data-testid="addr-name" required value={address.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
      <div className="flex">
        <span className="border border-r-0 border-vv-black/20 px-4 py-3 text-sm text-vv-muted bg-vv-elevated/50">+91</span>
        <input data-testid="addr-phone" value={address.phone} readOnly={lockPhone}
          onChange={(e) => !lockPhone && setAddress({ ...address, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
          placeholder="10-digit mobile" className={`${inputCls} flex-1 min-w-0 ${lockPhone ? "bg-vv-elevated/40 text-vv-muted" : ""}`} />
      </div>
      <div className="relative">
        <input data-testid="addr-pin" required inputMode="numeric" maxLength={6} value={address.pin}
          onChange={(e) => setAddress({ ...address, pin: e.target.value.replace(/\D/g, "") })}
          placeholder="PIN code" className={inputCls} />
        {pinState.loading && <Loader2 size={14} className="animate-spin absolute right-4 top-1/2 -translate-y-1/2 text-vv-stone" />}
      </div>
      {pinState.offices.length > 1 ? (
        <select data-testid="addr-locality" value={address.locality} onChange={set("locality")} className={inputCls}>
          {pinState.offices.map((o, i) => <option key={i} value={o.name}>{o.name}</option>)}
        </select>
      ) : <div className="hidden sm:block" />}
      <input data-testid="addr-city" required value={address.city} readOnly placeholder="City" className={`${inputCls} bg-vv-elevated/40`} />
      <input data-testid="addr-state" required value={address.state} readOnly placeholder="State" className={`${inputCls} bg-vv-elevated/40`} />
      <input data-testid="addr-house" required value={address.house} onChange={set("house")} placeholder="House / Flat number" className={inputCls} />
      <input data-testid="addr-street" required value={address.street} onChange={set("street")} placeholder="Building, street or area" className={inputCls} />
      <input data-testid="addr-landmark" value={address.landmark} onChange={set("landmark")} placeholder="Landmark (optional)" className={`${inputCls} sm:col-span-2`} />
      {pinState.error && <p data-testid="pin-error" className="text-xs text-vv-burgundy sm:col-span-2">{pinState.error}</p>}
      {pinState.serviceable === false && !pinState.error && (
        <p data-testid="pin-serviceability-warning" className="text-xs text-vv-espresso sm:col-span-2">
          This PIN may have limited delivery coverage — we will confirm by SMS if there is any issue.
        </p>
      )}
      <input type="hidden" value={canProceed ? "1" : "0"} data-testid="addr-valid" readOnly />
    </div>
  );
}

const EMPTY_ADDRESS = { name: "", phone: "", pin: "", city: "", state: "", locality: "", house: "", street: "", landmark: "", label: "Home" };

function AddressStep({ user, address, setAddress, onNext }) {
  const saved = user?.addresses || [];
  const [mode, setMode] = useState(saved.length ? "saved" : "new");
  const [selectedId, setSelectedId] = useState(saved[0]?.id || "");
  const valid = address.name && address.phone?.length === 10 && /^[1-9]\d{5}$/.test(address.pin)
    && address.city && address.state && address.house && address.street;

  const useSaved = () => {
    const a = saved.find((x) => x.id === selectedId);
    if (!a) return;
    setAddress({
      name: a.name, phone: (a.phone || "").replace(/^91/, ""), pin: a.pin, city: a.city, state: a.state,
      locality: a.locality || "", house: a.house, street: a.street, landmark: a.landmark || "", label: a.label || "Home",
    });
    onNext();
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-3">DELIVERY ADDRESS</h1>
      {saved.length > 0 && (
        <div className="flex gap-6 border-b border-vv-black/10 mb-8 mt-6">
          <button data-testid="address-tab-saved" onClick={() => setMode("saved")}
            className={`pb-3 text-[11px] uppercase tracking-[0.25em] transition-colors ${mode === "saved" ? "border-b border-vv-black -mb-px font-medium" : "text-vv-muted"}`}>
            Saved Addresses ({saved.length})
          </button>
          <button data-testid="address-tab-new" onClick={() => setMode("new")}
            className={`pb-3 text-[11px] uppercase tracking-[0.25em] transition-colors ${mode === "new" ? "border-b border-vv-black -mb-px font-medium" : "text-vv-muted"}`}>
            New Address
          </button>
        </div>
      )}
      {saved.length > 0 && mode === "saved" ? (
        <div className="space-y-4 mt-2">
          {saved.map((a) => (
            <label key={a.id} data-testid={`saved-address-${a.id}`}
              className={`flex gap-4 border p-5 cursor-pointer transition-colors ${selectedId === a.id ? "border-vv-black bg-vv-elevated/40" : "border-vv-black/15"}`}>
              <input type="radio" name="saved-address" checked={selectedId === a.id} onChange={() => setSelectedId(a.id)} className="accent-[#111111] mt-1" />
              <span className="text-sm leading-relaxed">
                <span className="flex items-center gap-2 font-medium">
                  <MapPin size={13} strokeWidth={1.5} className="text-vv-muted" /> {a.name}
                  <span className="text-[9px] uppercase tracking-[0.2em] text-vv-muted border border-vv-black/15 px-2 py-0.5">{a.label}</span>
                </span>
                <span className="block text-vv-muted mt-1.5">
                  {a.house}, {a.street}{a.landmark ? `, near ${a.landmark}` : ""}, {a.city}, {a.state} — {a.pin}
                </span>
                <span className="block text-vv-muted mt-0.5">+91 {(a.phone || "").replace(/^91/, "")}</span>
              </span>
            </label>
          ))}
          <button data-testid="use-saved-address-button" onClick={useSaved} className="vv-btn-dark w-full">
            Deliver to This Address
          </button>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (valid) onNext(); }}>
          <p className="text-sm text-vv-muted mb-8">Enter your PIN code first — we will fill your city and state automatically.</p>
          <AddressFields address={address} setAddress={setAddress} />
          <p className="text-[11px] text-vv-stone mt-4">This address will be saved to your account for faster checkout next time.</p>
          <button data-testid="address-continue-button" type="submit" disabled={!valid} className="vv-btn-dark w-full mt-4 disabled:opacity-50">
            Continue to Payment
          </button>
        </form>
      )}
    </div>
  );
}

export default function Checkout() {
  const { cart, subtotal, clearCart, user, setUser, authChecked, updateQty, removeItem, count, payConfig } = useShop();
  const navigate = useNavigate();
  const [step, setStep] = useState("verify");
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [payment, setPayment] = useState("cashfree");
  const [placing, setPlacing] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    if (authChecked && user?.phone) {
      setStep("address");
      setAddress((a) => ({
        ...a,
        name: a.name || user.name || "",
        phone: user.phone.replace(/^91/, ""),
        ...((!user.addresses?.length && user.saved_address) || {}),
      }));
    }
  }, [authChecked, user]);

  const discount = coupon && subtotal >= (coupon.min_order || 0)
    ? (coupon.type === "percent" ? Math.min(Math.round(subtotal * coupon.value / 100), subtotal) : Math.min(coupon.value, subtotal))
    : 0;

  useEffect(() => {
    if (coupon && subtotal < (coupon.min_order || 0)) {
      toast.error(`${coupon.code} needs a minimum order of ${inr(coupon.min_order)}`);
      setCoupon(null);
    }
  }, [subtotal, coupon]);

  if (cart.length === 0) {
    return (
      <main data-testid="checkout-empty" className="min-h-[60vh] flex flex-col items-center justify-center gap-6 px-4">
        <p className="font-display text-3xl">Your bag is empty.</p>
        <Link to="/shop" className="vv-btn-dark">Continue Shopping</Link>
      </main>
    );
  }

  const shipping = subtotal >= FREE_SHIP ? 0 : 99;
  const total = subtotal - discount + shipping;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), subtotal });
      setCoupon(data);
      setCouponInput("");
      toast.success(`${data.code} applied — you save ${inr(data.discount)}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setCouponBusy(false); }
  };

  const inc = (item) => {
    if (item.qty >= MAX_QTY) {
      toast.error(`Maximum ${MAX_QTY} pieces per style`);
      return;
    }
    updateQty(item.key, 1);
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const itemsPayload = cart.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, image: i.image, size: i.size, color: i.color, qty: i.qty }));
      const customerPayload = {
        name: address.name, phone: `91${address.phone}`, email: user?.email || null,
        address: address.house, address2: address.street, landmark: address.landmark,
        city: address.city, state: address.state, pin: address.pin, label: address.label || "Home",
      };
      if (payment === "cashfree") {
        const { data } = await api.post("/payments/cashfree/order", {
          items: itemsPayload, customer: customerPayload, coupon: coupon?.code || null,
        });
        const { load } = await import("@cashfreepayments/cashfree-js");
        const cashfree = await load({ mode: payConfig.env === "production" ? "production" : "sandbox" });
        await cashfree.checkout({
          paymentSessionId: data.payment_session_id,
          redirectTarget: "_self",
        });
        return;
      }
      const { data } = await api.post("/orders", {
        items: itemsPayload,
        customer: customerPayload,
        payment_method: payment,
        coupon: coupon?.code || null,
      });
      clearCart();
      navigate("/order-success", { state: { order: data } });
    } catch (err) {
      toast.error(formatApiError(err));
      setPlacing(false);
    }
  };

  const summary = (
    <aside className="lg:border-l lg:border-vv-black/10 lg:pl-12">
      <div className="flex items-center justify-between mb-6">
        <p className="vv-overline">Order Summary</p>
        <p data-testid="summary-item-count" className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">{count} {count === 1 ? "item" : "items"}</p>
      </div>
      <div className="space-y-5 max-h-96 overflow-y-auto pr-2">
        {cart.map((i) => (
          <div key={i.key} data-testid={`summary-item-${i.key}`} className="flex gap-4">
            <div className="w-14 h-[4.5rem] bg-vv-elevated overflow-hidden shrink-0">
              {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-2">
                <p className="text-xs font-medium truncate">{i.name}</p>
                <button data-testid={`summary-remove-${i.key}`} onClick={() => removeItem(i.key)} aria-label="Remove item"
                  className="text-vv-muted hover:text-vv-burgundy transition-colors shrink-0">
                  <X size={13} strokeWidth={1.5} />
                </button>
              </div>
              <p className="text-[10px] text-vv-muted mt-0.5">{i.color}{i.size ? ` · Size ${i.size}` : ""} · {inr(i.price)} each</p>
              <div className="flex items-center justify-between mt-2.5">
                <div className="flex items-center border border-vv-black/20">
                  <button data-testid={`summary-minus-${i.key}`} onClick={() => updateQty(i.key, -1)} disabled={i.qty <= 1}
                    className="px-2 py-1 disabled:opacity-30" aria-label="Decrease quantity">
                    <Minus size={11} />
                  </button>
                  <span data-testid={`summary-qty-${i.key}`} className="text-[11px] w-6 text-center">{i.qty}</span>
                  <button data-testid={`summary-plus-${i.key}`} onClick={() => inc(i)} disabled={i.qty >= MAX_QTY}
                    className="px-2 py-1 disabled:opacity-30" aria-label="Increase quantity">
                    <Plus size={11} />
                  </button>
                </div>
                <p data-testid={`summary-line-total-${i.key}`} className="text-xs font-medium">{inr(i.price * i.qty)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Link data-testid="summary-continue-shopping" to="/shop" className="inline-block text-[10px] uppercase tracking-[0.2em] text-vv-muted vv-link-underline mt-4">
        Continue Shopping
      </Link>
      <div className="mt-5">
        {coupon ? (
          <div data-testid="coupon-applied" className="flex items-center justify-between border border-vv-forest/40 bg-vv-forest/5 px-4 py-3">
            <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-vv-forest">
              <Tag size={12} strokeWidth={1.5} /> {coupon.code}
            </span>
            <button data-testid="coupon-remove-button" onClick={() => setCoupon(null)} aria-label="Remove coupon"
              className="text-vv-muted hover:text-vv-burgundy transition-colors">
              <X size={13} strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          <div className="flex">
            <input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())}
              placeholder="Discount code" className="flex-1 min-w-0 bg-transparent border border-r-0 border-vv-black/20 focus:border-vv-black outline-none px-4 py-2.5 text-xs tracking-[0.15em] placeholder:text-vv-stone uppercase" />
            <button type="button" data-testid="coupon-apply-button" onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()}
              className="border border-vv-black bg-vv-black text-vv-ivory px-5 text-[10px] uppercase tracking-[0.2em] hover:bg-vv-espresso transition-colors disabled:opacity-50">
              {couponBusy ? "…" : "Apply"}
            </button>
          </div>
        )}
      </div>
      <div className="border-t border-vv-black/10 mt-5 pt-5 space-y-2 text-sm">
        <div className="flex justify-between text-vv-muted"><span>Subtotal</span><span data-testid="summary-subtotal">{inr(subtotal)}</span></div>
        {discount > 0 && (
          <div className="flex justify-between text-vv-forest">
            <span>Discount ({coupon.code})</span>
            <span data-testid="summary-discount">− {inr(discount)}</span>
          </div>
        )}
        <div className="flex justify-between text-vv-muted"><span>Shipping</span><span data-testid="summary-shipping">{shipping === 0 ? "Free" : inr(shipping)}</span></div>
        <div className="flex justify-between font-medium text-base pt-2 border-t border-vv-black/10">
          <span>Total</span><span data-testid="checkout-total">{inr(total)}</span>
        </div>
        <p className="text-[10px] text-vv-stone pt-1">Inclusive of all taxes</p>
      </div>
    </aside>
  );

  return (
    <main data-testid="checkout-page" className="px-4 sm:px-8 lg:px-16 max-w-6xl mx-auto py-12 md:py-20">
      <Reveal>
        <p className="vv-overline text-vv-muted mb-4">Secure Checkout{payConfig.demo ? " — Demo Payment Available" : ""}{payConfig.cashfree && payConfig.env !== "production" ? " · Test Mode" : ""}</p>
        <Steps step={step} />
      </Reveal>
      {step === "verify" && (
        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_400px] gap-12">
          <VerifyStep onVerified={(u) => { setUser(u); setAddress((a) => ({ ...a, name: a.name || u.name || "", phone: (u.phone || "").replace(/^91/, "") })); setStep("address"); }} />
          {summary}
        </div>
      )}
      {step === "address" && (
        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_400px] gap-12">
          <AddressStep user={user} address={address} setAddress={setAddress} onNext={() => setStep("payment")} />
          {summary}
        </div>
      )}
      {step === "payment" && (
        <form onSubmit={placeOrder} className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_400px] gap-12">
          <div className="space-y-8">
            <section>
              <div className="flex items-center justify-between mb-5">
                <p className="vv-overline">Deliver To</p>
                <button type="button" data-testid="edit-address-button" onClick={() => setStep("address")} className="text-[10px] uppercase tracking-[0.2em] text-vv-muted vv-link-underline">Edit</button>
              </div>
              <div data-testid="confirm-address" className="border border-vv-black/10 p-5 text-sm leading-relaxed">
                <p className="font-medium flex items-center gap-2">
                  {address.name}
                  {address.label && <span className="text-[9px] uppercase tracking-[0.2em] text-vv-muted border border-vv-black/15 px-2 py-0.5">{address.label}</span>}
                </p>
                <p className="text-vv-muted mt-1">
                  {address.house}, {address.street}{address.landmark ? `, near ${address.landmark}` : ""}
                  {address.locality ? `, ${address.locality}` : ""}, {address.city}, {address.state} — {address.pin}
                </p>
                <p className="text-vv-muted mt-1">+91 {address.phone}</p>
              </div>
            </section>
            <section>
              <p className="vv-overline mb-5">{payConfig.demo ? "Payment" : "Payment — Secure Online"}</p>
              <div className="space-y-3">
                {payConfig.cashfree && (
                  <label data-testid="payment-cashfree"
                    className={`flex items-center gap-4 border p-4 cursor-pointer transition-colors ${payment === "cashfree" ? "border-vv-black bg-vv-elevated/50" : "border-vv-black/15"}`}>
                    <input type="radio" name="payment" checked={payment === "cashfree"} onChange={() => setPayment("cashfree")} className="accent-[#111111]" />
                    <span>
                      <span className="block text-sm font-medium">Pay Online</span>
                      <span className="block text-[11px] text-vv-muted">UPI, cards, netbanking & wallets — secure via Cashfree{payConfig.env !== "production" ? " (test mode)" : ""}</span>
                    </span>
                  </label>
                )}
                {payConfig.demo && [["upi", "UPI", "GPay, PhonePe, Paytm and more"], ["card", "Card", "Visa, Mastercard, RuPay"], ["cod", "Cash on Delivery", "Pay when it arrives"]].map(([val, label, sub]) => (
                  <label key={val} data-testid={`payment-${val}`}
                    className={`flex items-center gap-4 border p-4 cursor-pointer transition-colors ${payment === val ? "border-vv-black bg-vv-elevated/50" : "border-vv-black/15"}`}>
                    <input type="radio" name="payment" checked={payment === val} onChange={() => setPayment(val)} className="accent-[#111111]" />
                    <span>
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-[11px] text-vv-muted">{sub}</span>
                    </span>
                  </label>
                ))}
              </div>
              {payConfig.demo && <p className="text-[11px] text-vv-stone mt-3">Demo checkout — no real payment is charged. Choose "Pay Online" for a real payment.</p>}
            </section>
            <button data-testid="place-order-button" type="submit" disabled={placing} className="vv-btn-dark w-full disabled:opacity-50">
              {placing ? "Please wait…" : payment === "cashfree" ? `Pay ${inr(total)} Securely` : `Pay ${inr(total)}`}
            </button>
          </div>
          {summary}
        </form>
      )}
    </main>
  );
}

export function OrderSuccess() {
  const { state } = useLocation();
  const order = state?.order;
  const contact = order?.customer?.email || (order?.customer?.phone ? `+91 ${order.customer.phone.replace(/^91/, "")}` : "");
  return (
    <main data-testid="order-success-page" className="min-h-[70vh] flex flex-col items-center justify-center px-4 text-center py-20">
      <Reveal>
        <CheckCircle2 size={40} strokeWidth={1} className="text-vv-forest mx-auto mb-8" />
        <p className="vv-overline text-vv-muted mb-4">Order Confirmed</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">THANK YOU.</h1>
        {order && (
          <p data-testid="order-ref" className="mt-6 text-sm text-vv-muted">
            Order reference <span className="font-mono text-vv-black">{order.ref}</span> — confirmation noted for {contact}.
          </p>
        )}
        <p className="text-sm text-vv-muted mt-2">{order.payment_method === "cashfree" ? "Payment received." : "Demo order — no payment was charged."}</p>
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <Link data-testid="success-continue-shopping" to="/shop" className="vv-btn-dark">Continue Shopping</Link>
          <Link data-testid="success-track-order" to="/account?tab=track" className="vv-btn-outline-dark">Track Order</Link>
        </div>
      </Reveal>
    </main>
  );
}
